export const ARTISAN_CAREER_NAME = 'Artisan'
export const REFINEMENT_PREFIX = 'Raffinement - '
export const ARTISANAT_SKILL_NAME = 'Artisanat'
export const ARTISANAT_FREE_UNITS = 2

/** Paid XP a career must already hold before a skill of that tier unlocks. */
export const TIER_REQUIREMENTS: Record<number, number> = { 2: 50, 3: 150 }

export type SkillXpInput = {
	skillId: string
	rank: number | null
	skill: {
		name?: string | null
		cost?: number | null
		maxPurchase?: number | null
		careers?: Array<{ careerId: string; isStarting: boolean }> | null
	}
}

export type SkillXpEntry<T extends SkillXpInput = SkillXpInput> = {
	characterSkill: T
	rank: number
	costPerUnit: number
	freeUnits: number
	paidUnits: number
	paidXp: number
	totalXp: number
	maxPurchase: number | null
}

function isRefinement(name: string | null | undefined) {
	return name?.startsWith(REFINEMENT_PREFIX) ?? false
}

/**
 * The single refinement an Artisan gets for free. Picked by name rather than by
 * array position: callers load skills with different orderings, and the free
 * unit must land on the same refinement everywhere it is displayed.
 */
export function findPrimaryRefinementId(
	careerName: string | null | undefined,
	skills: SkillXpInput[],
) {
	if (careerName !== ARTISAN_CAREER_NAME) return null
	const owned = skills
		.filter((entry) => isRefinement(entry.skill.name) && (entry.rank ?? 0) > 0)
		.sort((a, b) => (a.skill.name ?? '').localeCompare(b.skill.name ?? ''))
	return owned[0]?.skillId ?? null
}

/**
 * Free/paid split of every owned skill. Shared by the character sheet and the
 * admin editor so both report the same spend.
 */
export function computeSkillXpBreakdown<T extends SkillXpInput>({
	careerId,
	careerName,
	skills,
}: {
	careerId: string | null | undefined
	careerName: string | null | undefined
	skills: T[]
}): Array<SkillXpEntry<T>> {
	const isArtisan = careerName === ARTISAN_CAREER_NAME
	const primaryRefinementId = findPrimaryRefinementId(careerName, skills)

	return skills.map((characterSkill) => {
		const rank = Math.max(0, characterSkill.rank ?? 0)
		const costPerUnit = characterSkill.skill.cost ?? 0
		let freeUnits = 0

		if (rank > 0 && careerId != null) {
			const link = characterSkill.skill.careers?.find(
				(entry) => entry.careerId === careerId,
			)
			const refinement = isArtisan && isRefinement(characterSkill.skill.name)
			freeUnits = (link?.isStarting ?? false) && !refinement ? 1 : 0

			if (refinement) {
				freeUnits = characterSkill.skillId === primaryRefinementId ? 1 : 0
			}

			if (isArtisan && characterSkill.skill.name === ARTISANAT_SKILL_NAME) {
				freeUnits = Math.max(freeUnits, ARTISANAT_FREE_UNITS)
			}
		}

		const paidUnits = Math.max(0, rank - freeUnits)

		return {
			characterSkill,
			rank,
			costPerUnit,
			freeUnits,
			paidUnits,
			paidXp: paidUnits * costPerUnit,
			totalXp: rank * costPerUnit,
			maxPurchase: characterSkill.skill.maxPurchase ?? null,
		}
	})
}

export function sumPaidXp(entries: Array<SkillXpEntry<SkillXpInput>>) {
	return entries.reduce((total, entry) => total + entry.paidXp, 0)
}

/**
 * The career a skill's paid XP counts towards for tier gating: the character's
 * own career when it teaches the skill, otherwise the skill's first career.
 */
export function resolveOwnerCareerId(
	entry: SkillXpEntry<SkillXpInput>,
	careerId: string | null | undefined,
) {
	const careers = entry.characterSkill.skill.careers ?? []
	if (careerId != null && careers.some((link) => link.careerId === careerId)) {
		return careerId
	}
	return careers[0]?.careerId ?? null
}

export function sumPaidXpByCareer(
	entries: Array<SkillXpEntry<SkillXpInput>>,
	careerId: string | null | undefined,
) {
	const totals = new Map<string, number>()
	for (const entry of entries) {
		if (entry.paidXp <= 0) continue
		const ownerCareerId = resolveOwnerCareerId(entry, careerId)
		if (ownerCareerId == null) continue
		totals.set(ownerCareerId, (totals.get(ownerCareerId) ?? 0) + entry.paidXp)
	}
	return totals
}
